import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Spin, Tag, Tabs, Badge, Button, Empty } from "antd";
import {
  UserOutlined, ShoppingOutlined, DollarOutlined, HistoryOutlined, SendOutlined,
  StopOutlined, SwapOutlined, RollbackOutlined, NotificationOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../../contexts/AuthContext";
import { useSocket } from "../../../contexts/SocketContext";
import { movableBackStatuses } from "../modals-misc/MoveStatusModal";
import { URGENCY_CFG } from "../../pipeline";
import { useTaskDetail } from "../../viewmodels/shared/useTaskDetail";
import { useAgentsUsers } from "../../viewmodels/shared/useAgentsUsers";
import { OverviewTab } from "./OverviewTab";
import { PartsTab } from "./PartsTab";
import { PaymentHandoverTab } from "./PaymentHandoverTab";
import { HistoryTab } from "./HistoryTab";
import { SentSuppliersTab } from "./SentSuppliersTab";
import { UnavailablePartsModal } from "../modals-misc/UnavailablePartsModal";
import { BillingDetailsModal } from "../modals-misc/BillingDetailsModal";
import { SendToSuppliersModal } from "../suppliers/SendToSuppliersModal";
import { PriceUpdateDrawer } from "../price-drawer/PriceUpdateDrawer";
import { useAccess } from "../../../../features/access/context/AccessProvider";

export function WorkflowTaskDrawer({ queryPk, onClose, onSent, onReassignClick, onUnparkClick, onMoveBackClick, onCancelClick, onBillingDetailsSent, onSendModalOpenChange, onOpenPaymentModal }) {
  const { authState } = useContext(AuthContext);
  const { can } = useAccess();
  const { socket: mainSocket } = useSocket();
  const { data, loading, refetch } = useTaskDetail(queryPk, { userId: authState?.id });
  const { users } = useAgentsUsers();
  const userMap = useMemo(() => Object.fromEntries(users.map(u => [u.id, u.fullName])), [users]);

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [unavailableModalOpen, setUnavailableModalOpen] = useState(false);
  const [billingDetailsModalOpen, setBillingDetailsModalOpen] = useState(false);
  const [priceDrawerOpen, setPriceDrawerOpen] = useState(false);

  useEffect(() => {
    onSendModalOpenChange?.(sendModalOpen);
  }, [sendModalOpen, onSendModalOpenChange]);

  const queryPkRef = useRef(queryPk);
  useEffect(() => { queryPkRef.current = queryPk; }, [queryPk]);

  const checkBillingDetailsNeeded = (freshData) => {
    const c = freshData?.task?.customer;
    const isPaid = freshData?.after_sales?.payment_status === "paid";
    if (!isPaid || !c) return;
    const hasRealName = !!String(c.customer_real_name || "").trim();
    const hasAddress = Array.isArray(c.customer_addresses_new) && c.customer_addresses_new.length > 0;
    if (!hasRealName || !hasAddress) setBillingDetailsModalOpen(true);
  };

  // Silently refresh when quotes, parts, or task-level updates arrive
  useEffect(() => {
    if (!mainSocket) return;
    const handle = (evtData) => {
      const pk = evtData?.query_pk ?? evtData?.queryPk ?? evtData?.data?.task_id ?? evtData?.data?.query_pk ?? evtData?.data?.payment?.query_pk;
      if (pk && pk === queryPkRef.current) refetch();
    };
    mainSocket.on("query:quotes_updated", handle);
    mainSocket.on("query:parts_updated", handle);
    mainSocket.on("query:updated", handle);
    mainSocket.on("query:payment_manually_received", handle);
    mainSocket.on("query:payment_updated", handle);
    mainSocket.on("query:after_sales_updated", handle);
    return () => {
      mainSocket.off("query:quotes_updated", handle);
      mainSocket.off("query:parts_updated", handle);
      mainSocket.off("query:updated", handle);
      mainSocket.off("query:payment_manually_received", handle);
      mainSocket.off("query:payment_updated", handle);
      mainSocket.off("query:after_sales_updated", handle);
    };
  }, [mainSocket]); // eslint-disable-line

  const task         = data?.task         ?? null;
  const parts        = data?.parts        ?? [];
  const zeroQuoteParts = parts.filter(p => (p.supplier_quotes ?? p.quotes ?? []).length === 0);
  const afterSales   = data?.after_sales  ?? null;
  const movements    = data?.movements    ?? [];
  const urgCfg       = task?.urgency ? URGENCY_CFG[task.urgency] : null;

  const sendAttempts = Array.isArray(task?.supplier_send_info) ? task.supplier_send_info : [];
  const initialGroups = [...new Set(sendAttempts.flatMap(a => a.selected_groups ?? []))];
  const previouslyContactedIds = new Set(sendAttempts.flatMap(a => (a.suppliers ?? []).map(s => s.supplier_id)));

  const tabs = [
    {
      key: "overview",
      label: "Overview",
      icon: <UserOutlined />,
      children: task ? <OverviewTab task={task} afterSales={afterSales} parts={parts} userMap={userMap} /> : null,
    },
    {
      key: "sent",
      label: (
        <Badge count={sendAttempts.length} size="small" offset={[6, 0]} color="#fa8c16">
          <span style={{ paddingRight: 6 }}>Sent</span>
        </Badge>
      ),
      icon: <SendOutlined />,
      children: <SentSuppliersTab attempts={sendAttempts} parts={parts} userMap={userMap} onSendMore={() => setSendModalOpen(true)} />,
    },
    {
      key: "parts",
      label: (
        <Badge count={parts.filter(p => p.status !== "disabled").length} size="small" offset={[6, 0]} color="#1677ff">
          <span style={{ paddingRight: 6 }}>Parts</span>
        </Badge>
      ),
      icon: <ShoppingOutlined />,
      children: <PartsTab
        parts={parts} images={task?.images || []} queryPk={queryPk} userId={authState?.id}
        customerPhone={task?.customer?.customer_mobile}
        onPartPatched={refetch} onManagePrices={() => setPriceDrawerOpen(true)}
      />,
    },
    {
      key: "payments",
      label: (
        <span>Payment &amp; Handover</span>
      ),
      icon: <DollarOutlined />,
      children: <PaymentHandoverTab
        paymentVisible={can("payments.view")}
        paymentProps={{
          onOpenPaymentModal: () => onOpenPaymentModal?.({ id: queryPk, query_id: task?.query_id, status: task?.status, customer: task?.customer }),
        }}
        handoverProps={{ queryPk, customer: task?.customer, priority: task?.urgency || 1, canSubmit: can("sales_pipeline.handover_to_operations") }}
      />,
    },
    {
      key: "history",
      label: (
        <Badge count={movements.length} size="small" offset={[6, 0]} color="#8c8c8c">
          <span style={{ paddingRight: 6 }}>History</span>
        </Badge>
      ),
      icon: <HistoryOutlined />,
      children: <HistoryTab movements={movements} />,
    },
  ];

  return (
    <>
    <Drawer
      open={!!queryPk}
      onClose={onClose}
      width={720}
      push={false}
      destroyOnHidden
      title={
        task ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700 }}>#{task.query_id}</span>
            <Tag color="blue" style={{ margin: 0 }}>{task.status_name || task.status}</Tag>
            {urgCfg && (
              <Tag style={{ margin: 0, color: urgCfg.color, borderColor: urgCfg.color, background: urgCfg.pill }}>
                {urgCfg.label}
              </Tag>
            )}
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              {onUnparkClick && ["parked", "on_hold", "postponed"].includes(task?.status) && (
                <Button size="small" icon={<RollbackOutlined />} onClick={() => onUnparkClick(queryPk)}>
                  Unpark
                </Button>
              )}
              {onReassignClick && can("sales_pipeline.reassign_query") && (
                <Button size="small" icon={<SwapOutlined />} onClick={() => onReassignClick(queryPk)}>
                  Reassign
                </Button>
              )}
              {onMoveBackClick && can("sales_pipeline.change_status") && task?.status !== "confirmed_waiting_payment" && movableBackStatuses(task?.status).length > 0 && (
                <Button size="small" icon={<RollbackOutlined />} onClick={() => onMoveBackClick(queryPk, task?.query_id, task?.status)}>
                  Move Back
                </Button>
              )}
              {task?.status !== "draft" && zeroQuoteParts.length > 0 && (
                <Button size="small" icon={<NotificationOutlined />} onClick={() => setUnavailableModalOpen(true)}>
                  Notify Unavailable Parts
                </Button>
              )}
              {onCancelClick && can("sales_pipeline.change_status") && !["cancelled", "completed", "disabled"].includes(task?.status) && (
                <Button size="small" danger icon={<StopOutlined />} onClick={() => onCancelClick(queryPk, task?.query_id)}>
                  Mark as Lost
                </Button>
              )}
            </div>
          </div>
        ) : "Task"
      }
      styles={{ body: { padding: "0 20px 20px", overflowY: "auto" } }}
    >
      {loading && !task && (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && !task && queryPk && (
        <Empty description="Task not found" style={{ padding: 60 }} />
      )}

      {task && (
        <Tabs
          defaultActiveKey="overview"
          size="small"
          style={{ marginTop: 4 }}
          items={tabs}
        />
      )}
    </Drawer>

    <SendToSuppliersModal
      queryPk={queryPk}
      task={task}
      open={sendModalOpen}
      userId={authState?.id}
      initialGroups={initialGroups}
      previouslyContactedIds={previouslyContactedIds}
      onClose={() => setSendModalOpen(false)}
      onSent={(supplierIds) => { onSent?.(queryPk, supplierIds); }}
    />

    <PriceUpdateDrawer
      card={task ? { id: queryPk, query_id: task.query_id, status: task.status, customer: task.customer, vehicle: task.vehicle } : null}
      open={priceDrawerOpen}
      onClose={() => { setPriceDrawerOpen(false); refetch(); }}
      userId={authState?.id}
      authState={authState}
      userMap={userMap}
    />

    <UnavailablePartsModal
      open={unavailableModalOpen}
      queryPk={queryPk}
      queryId={task?.query_id}
      customerName={task?.customer?.customer_name}
      customerPhone={task?.customer?.customer_mobile}
      unavailableParts={zeroQuoteParts}
      userId={authState?.id}
      onClose={() => setUnavailableModalOpen(false)}
      onSuccess={refetch}
    />

    <BillingDetailsModal
      open={billingDetailsModalOpen}
      queryPk={queryPk}
      queryId={task?.query_id}
      customerName={task?.customer?.customer_real_name || task?.customer?.customer_name}
      customerPhone={task?.customer?.customer_mobile}
      userId={authState?.id}
      onClose={() => setBillingDetailsModalOpen(false)}
      onSuccess={() => {
        setBillingDetailsModalOpen(false);
        onBillingDetailsSent?.(task?.customer?.customer_mobile);
      }}
    />
  </>
  );
}
